import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from '../lib/api';
import { useAuth } from '../lib/useAuth';

/**
 * Where to go after signing in here.
 *
 * This page is mounted at /admin/login only — the member sign-in is a separate
 * template page at /login — so the destination is the panel, not the public
 * site. RequireAdmin already records where the visitor was heading before it
 * bounced them here, and honouring it is the difference between landing on the
 * page you asked for and having to find it again.
 *
 * Only in-app admin paths are accepted. The state is set by our own guard
 * rather than by anything a visitor controls, but a redirect target read from
 * somewhere else is worth being narrow about on principle.
 */
function destinationFrom(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && /^\/admin(\/|$)/.test(from) ? from : '/admin';
}

export function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        // `replace`, so Back does not return to a login form the visitor has
        // already used.
        navigate(destinationFrom(location.state), { replace: true });
      } else {
        // Registering here creates a member, not a staff account, so the panel
        // would only turn them away. They belong on the public site.
        await register({ email, password });
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unable to sign in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <div className="rounded-xl bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          {mode === 'login' ? 'Sign in' : 'Create an account'}
        </h1>

        <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">Password</span>
            <input
              type="password"
              required
              minLength={mode === 'register' ? 8 : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
            />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={busy} className="btn-brand w-full">
            {busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
          className="mt-4 text-sm text-brand-600 hover:underline"
        >
          {mode === 'login' ? "Don't have an account? Register" : 'Have an account? Sign in'}
        </button>
      </div>
    </div>
  );
}
